import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { auth } from "../../utils/firebase";
import { dashboardPath } from "../../constants";

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const mainStyle = {
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    flexDirection: "column",
    fontFamily: theme.typography.fontFamily,
    paddingTop: "var(--nav-height)",
    minHeight: "100vh",
    boxSizing: "border-box",
    backgroundColor: theme.palette.brand.linen,
  };

  const eyebrowStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.brand.brass,
    margin: "0 0 6px",
    textAlign: "center",
  };

  // Same field styling as the Contact page's form - Montserrat/Navy labels,
  // Source Sans input text, Warm Stone borders that turn Bethel Blue with a
  // pale mist glow on focus - applied once here so it cascades to both
  // TextFields via descendant selectors.
  const fieldStyle = {
    "& .MuiInputLabel-root": {
      fontFamily: theme.typography.fontFamily,
      fontWeight: 600,
      color: theme.palette.primary.dark,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiOutlinedInput-root": {
      fontFamily: theme.typography.paragraphFontFamily,
      color: theme.palette.text.primary,
      borderRadius: "6px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.brand.border,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.brand.mist}`,
    },
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);

    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        const redirectTo = location.state?.from?.pathname || dashboardPath;
        navigate(redirectTo, { replace: true });
      })
      .catch(() => {
        setError("Incorrect email or password.");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div style={mainStyle}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          ...fieldStyle,
          width: "100%",
          maxWidth: "360px",
          padding: "36px 28px",
          boxSizing: "border-box",
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.brand.border}`,
          borderRadius: "10px",
        }}
      >
        <div style={eyebrowStyle}>Bethel Bible Church</div>
        <h1
          style={{
            fontFamily: theme.typography.secondaryFontFamily,
            color: theme.palette.primary.dark,
            fontSize: "30px",
            fontWeight: 600,
            margin: "0 0 24px",
            textAlign: "center",
          }}
        >
          Admin Login
        </h1>

        <TextField
          label="Email"
          type="email"
          name="username"
          autoComplete="username"
          fullWidth
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          size="small"
          sx={{ marginBottom: "16px" }}
        />
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          name="password"
          autoComplete="current-password"
          fullWidth
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          size="small"
          sx={{ marginBottom: "16px" }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? (
                    <VisibilityOff sx={{ fontSize: "20px", color: theme.palette.text.secondary }} />
                  ) : (
                    <Visibility sx={{ fontSize: "20px", color: theme.palette.text.secondary }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Alert severity="error" sx={{ marginBottom: "16px" }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={submitting}
          sx={{ borderRadius: "6px", padding: "10px 0" }}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : "Log In"}
        </Button>
      </Box>
    </div>
  );
};

export default Login;
